import { Skeleton } from './ui/skeleton';
import { useSiteSettings } from '@/hooks/useSiteSettings';

interface CardLogoProps {
  logoUrl?: string | null;
  bankName: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  priority?: boolean;
  width?: number;
  height?: number;
}

export function CardLogo({ logoUrl, bankName, className = "w-12 h-8", loading = "lazy", priority = false, width = 48, height = 32 }: CardLogoProps) {
  const { data: settings } = useSiteSettings();
  const effectiveLoading = priority ? 'eager' : loading;
  
  // Trim whitespace from logoUrl
  const trimmedLogoUrl = logoUrl?.trim();
  
  // Validate logo URL - allow relative paths
  let validLogoUrl = trimmedLogoUrl;
  if (validLogoUrl) {
    try {
      // Only validate if it looks like an absolute URL
      if (validLogoUrl.startsWith('http://') || validLogoUrl.startsWith('https://')) {
        new URL(validLogoUrl);
      }
      // Relative paths are OK, skip validation
    } catch (error) {
      console.warn('Invalid logo URL for', bankName, ':', validLogoUrl);
      validLogoUrl = undefined; // Treat as invalid
    }
  }
  
  // Detect if className already has object-fit class
  const hasObjectFit = /object-(contain|cover|fill|none|scale-down)/.test(className);
  const baseClass = `${className} ${hasObjectFit ? '' : 'object-contain'} rounded-sm`;
  
  if (!validLogoUrl) {
    if (settings?.cardFallbackLogo) {
      return (
        <img
          src={settings.cardFallbackLogo}
          alt="Card placeholder"
          className={`${baseClass} opacity-40 grayscale`}
          width={width || 120}
          height={height || 40}
          loading={effectiveLoading}
          fetchPriority={priority ? "high" : "low"}
        />
      );
    }
    return <Skeleton className={`${className} rounded-sm`} />;
  }

  return (
    <img
      src={validLogoUrl}
      alt={bankName}
      className={baseClass}
      width={width || 120}
      height={height || 40}
      sizes="64px"
      loading={effectiveLoading}
      fetchPriority={priority ? "high" : "low"}
      style={width && height ? { aspectRatio: `${width}/${height}` } : { aspectRatio: '3/1' }}
      onError={() => {
        console.warn('Failed to load logo for', bankName, ':', validLogoUrl);
      }}
    />
  );
}
